import { IAdvertisementResponse } from '@/api/services/advertisements/advertisementsService'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/forms/form/Form'
import FormWrap from '@/components/forms/form/FormWrap'
import UploadArea from '@/components/forms/inputs/file/FileUpload'
import { Multiselect, Option } from '@/components/forms/inputs/select/MultiSelect'
import { Input } from '@/components/forms/inputs/text/Input'
import BigContainer from '@/components/layout/containers/BigContainer'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoaderIcon } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import AutoInput from './AutoInput'
import AutoRandomInput from './AutoRandomInputs'
import FontPicker from './FontPicker'
import { FormSchema, generatorFormSchema } from './generatorFormSchema'
import ImportLayout from './ImportLayout'

interface IProps {
  onSubmit: (data: FormSchema) => void
  jobPending: boolean
  resultIds?: string[]
  openResultPopup: () => void
  data?: FormSchema
  jobData: null | IAdvertisementResponse['job']
  setJobData: (data: IAdvertisementResponse['job'] | null) => void
}

const resolutionOptions: Option[] = [
  { value: '1024x1024' as const, label: '1024x1024' },
  { value: '1024x1536' as const, label: '1024x1536' },
  { value: '1536x1024' as const, label: '1536x1024' }
]

function GeneratorForm({
  onSubmit,
  jobPending,
  resultIds,
  openResultPopup,
  data,
  jobData,
  setJobData
}: IProps) {
  const [baseLoading, setBaseLoading] = useState(false)
  const form = useForm<FormSchema>({
    resolver: zodResolver(generatorFormSchema),
    defaultValues: {
      shopLogoFileTmp: data?.shopLogoFileTmp || '',
      templateFileTmp: data?.templateFileTmp || '',
      productImageFileTmp: data?.productImageFileTmp || '',
      mainColors: data?.mainColors || '',
      creationDescription: data?.creationDescription || '',
      productName: data?.productName || '',
      adSlogan: data?.adSlogan || '',
      price: data?.price || '',
      priceDescription: data?.priceDescription || '',
      priceLast30Days: data?.priceLast30Days || '',
      quality: data?.quality || 'UPSCALE_4X',
      resolution: data?.resolution || ['1536x1024'],
      areProductInscriptionsCrucial: data?.areProductInscriptionsCrucial || false,
      preferredFontHeadHtml: data?.preferredFontHeadHtml || ''
    }
  })

  const getLayoutImage = () => {
    const response = {
      templateFileTmp: 'd832cffe-9472-4575-95f9-bf6496040e3e'
    }
    form.setValue('templateFileTmp', response.templateFileTmp)
  }

  const getDataFromDatabase = () => {
    setBaseLoading(true)
    const response = {
      productImageFileTmp: 'fce11fa9-50bc-4958-aa5f-dc82f9913167',
      price: '1.99',
      priceLast30Days: '3.99'
    }
    setTimeout(() => {
      form.setValue('productImageFileTmp', response.productImageFileTmp)
      form.setValue('price', response.price)
      form.setValue('priceLast30Days', response.priceLast30Days)
      setBaseLoading(false)
    }, 1500)
  }

  useEffect(() => {
    setJobData({
      ...jobData,
      productImageFileId: form.getValues('productImageFileTmp')
    } as unknown as IAdvertisementResponse['job'])
  }, [form.watch('productImageFileTmp')])

  useEffect(() => {
    setJobData({
      ...jobData,
      shopLogoFileId: form.getValues('shopLogoFileTmp')
    } as unknown as IAdvertisementResponse['job'])
  }, [form.watch('shopLogoFileTmp')])

  useEffect(() => {
    setJobData({
      ...jobData,
      templateFileId: form.getValues('templateFileTmp')
    } as unknown as IAdvertisementResponse['job'])
  }, [form.watch('templateFileTmp')])

  return (
    <Form {...form}>
      <FormWrap onSubmit={form.handleSubmit(onSubmit)}>
        <BigContainer>
          <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-3">
            <FormField
              control={form.control}
              name="productImageFileTmp"
              render={({ field }) => (
                <FormItem>
                  <UploadArea
                    name={field.name}
                    onChange={field.onChange}
                    title="Wgraj zdjęcie produktu"
                    value={field.value}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shopLogoFileTmp"
              render={({ field }) => (
                <FormItem>
                  <UploadArea
                    name={field.name}
                    onChange={field.onChange}
                    title="Wgraj logo producenta"
                    value={field.value}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="templateFileTmp"
              render={({ field }) => (
                <FormItem>
                  <UploadArea
                    name={field.name}
                    onChange={field.onChange}
                    title="Wgraj zdjęcie szablonu"
                    value={field.value}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-18">
            <div className="flex w-full flex-col gap-6">
              <FormField
                control={form.control}
                name="creationDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opisz kreację</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Wpisz opis kreacji"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mainColors"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opisz kolory</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Wpisz kolory"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="adSlogan"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nagłówek reklamy</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Wpisz nagłówek reklamy"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FontPicker form={form} />
            </div>
            {/* RIGHT */}
            <div className="flex w-full flex-col gap-6">
              <FormField
                control={form.control}
                name="productName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nazwa produktu (opcjonalnie)</FormLabel>
                    <FormControl>
                      <div className="flex w-full flex-row gap-2">
                        <Input
                          placeholder="Wpisz nazwę produktu"
                          type="text"
                          {...field}
                          // error={errors?.creationDescription}
                        />
                        <Button
                          className="h-11 text-xs"
                          disabled={!field.value || baseLoading}
                          onClick={getDataFromDatabase}
                          type="button"
                        >
                          {baseLoading ? (
                            <LoaderIcon className="animate-spin" height={16} width={16} />
                          ) : null}
                          Zaciągnij dane z bazy
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cena produktu</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Wpisz cenę produktu"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="priceDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opis do ceny (opcjonalnie)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Wpisz opis ceny produktu"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="priceLast30Days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cena sprzed 30 dni</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Podaj cenę sprzed 30 dni"
                        type="text"
                        {...field}
                        // error={errors?.creationDescription}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="resolution"
                render={({ field }) => {
                  const currentSelectedValues = Array.isArray(field.value)
                    ? field.value
                    : []
                  const selectedOptions = resolutionOptions.filter((option) =>
                    currentSelectedValues.includes(option.value)
                  )
                  return (
                    <FormItem>
                      <FormLabel>Rozdzielczość</FormLabel>
                      <Multiselect
                        className="w-full"
                        onChange={(newSelectedOptions: Option[]) => {
                          field.onChange(newSelectedOptions.map((option) => option.value))
                        }}
                        options={resolutionOptions}
                        placeholder="Wybierz rozdzielczości"
                        selected={selectedOptions}
                      />
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            </div>
          </div>
        </BigContainer>
        <div
          className={cn(
            'mx-auto mt-4 grid w-full max-w-6xl grid-cols-1 gap-4 px-2 md:mt-10 md:px-0',
            resultIds?.length ? 'md:grid-cols-5' : 'md:grid-cols-4'
          )}
        >
          <ImportLayout onClick={getLayoutImage} />
          <AutoRandomInput setValue={form.setValue} />
          <AutoInput image={form.watch('productImageFileTmp')} setValue={form.setValue} />
          {resultIds?.length ? (
            <>
              <Button disabled={jobPending} onClick={openResultPopup} type="button">
                Wyświetl wyniki
              </Button>
              <Button disabled={jobPending} type="submit">
                Generuj ponownie
              </Button>
            </>
          ) : (
            <Button disabled={jobPending} type="submit">
              Generuj grafikę
            </Button>
          )}
        </div>
      </FormWrap>
    </Form>
  )
}

export default memo(GeneratorForm)
